/* =========================================================================
   CONSTANTES DEL PANEL DE ALIADOS

   «Aliado» es quien trabaja CON la plataforma y no EN ella. Hoy son dos
   oficios distintos con la misma puerta de entrada:

     · el conjunto  — administrador y celadores (roles 5 y 6);
     · el negocio   — el tendero (rol 2).

   Comparten armazón, sesión y menú; no comparten ni una pantalla. Lo que
   decide cuál de los dos se está usando es el ÁMBITO, y sale del rol.

   Los identificadores de rol son los de la tabla `rol` del backend, que NO es
   autoincremental: están puestos a mano (1 comprador, 2 tendero,
   3 domiciliario, 4 personal interno, 5 dueño de conjunto, 6 celador).
   ======================================================================== */

export const ROL_TENDERO = 2;
export const ROL_DUENO_CONJUNTO = 5;
export const ROL_CELADOR = 6;

export const AMBITO_CONJUNTO = "conjunto";
export const AMBITO_NEGOCIO = "negocio";

/** A qué ámbito pertenece cada rol. Lo que no esté acá no entra al panel. */
export const AMBITO_POR_ROL = {
  [ROL_TENDERO]: AMBITO_NEGOCIO,
  [ROL_DUENO_CONJUNTO]: AMBITO_CONJUNTO,
  [ROL_CELADOR]: AMBITO_CONJUNTO,
};

/** Quiénes pueden entrar. El 4 —personal interno— NO: tiene el suyo, y
    compartir la puerta ampliaría en silencio quién llega a la administración
    de la plataforma. */
export const ROLES_DE_ALIADO = Object.keys(AMBITO_POR_ROL).map(Number);

/** Se conserva el nombre anterior: lo usaba el contexto de sesión. */
export const ROLES_DE_CONJUNTO = [ROL_DUENO_CONJUNTO, ROL_CELADOR];

export const ambitoDeRol = (rol) => AMBITO_POR_ROL[Number(rol)] ?? null;

export const NOMBRE_DEL_ROL = {
  dueno: "Administrador del conjunto",
  celador: "Portería",
  tendero: "Mi negocio",
};

/** Cómo se identificó un domiciliario al entrar a un conjunto. Son dos
    niveles de confianza distintos y por eso se distinguen en pantalla: el
    código lo genera su app y caduca en cinco minutos; la cédula solo prueba
    que alguien dijo un número. */
export const METODOS_DE_ENTRADA = {
  codigo: { label: "Código", tone: "ok" },
  cedula: { label: "Cédula", tone: "warn" },
  /* Lo anotó el celador. No hay nada que verificar: quien entra no es usuario
     de la plataforma. Ni verde ni naranja — no es mejor ni peor control, es
     otra cosa. */
  manual: { label: "Anotado", tone: "neutral" },
};

/**
 * Qué clase de entrada es.
 *
 * La portería del conjunto no es sólo de VeciPa'Ya: por la puerta pasan visitas,
 * personal de servicio y domicilios de otras plataformas. Sólo la primera la
 * puede VERIFICAR el sistema; las demás las ANOTA el celador, y la distinción
 * tiene que verse porque son dos niveles de certeza distintos.
 */
export const CLASES_DE_ENTRADA = {
  domiciliario: { label: "Domiciliario", tone: "ok", icon: "bike" },
  visitante: { label: "Visita", tone: "info", icon: "users" },
  servicio: { label: "Servicio", tone: "neutral", icon: "cog" },
  domicilio_externo: { label: "Otra app", tone: "warn", icon: "bike" },
};

/* ---------------------------- LOS PEDIDOS ----------------------------- */

/**
 * Los cinco estados, con el nombre que usa quien atiende el mostrador.
 *
 * `accion` es lo que la tienda puede hacer desde ese estado, y `null` cuando
 * no le toca a ella. El 3 → 4 es el caso que importa: entregar solo lo declara
 * el domiciliario, porque es la transición que mueve dinero —registra el cobro
 * en efectivo y la ganancia de quien llevó—. El botón no existe acá, y el
 * servidor lo rechaza igual si alguien lo intenta.
 */
export const ESTADOS_DE_PEDIDO = {
  1: {
    clave: "nuevo",
    label: "Sin aceptar",
    corto: "Nuevos",
    tone: "warn",
    icon: "bell",
    accion: { a: 2, label: "Aceptar", tone: "primary" },
    /* Lo que se le está diciendo al comprador mientras el pedido está acá. */
    espera: "El cliente está esperando confirmación",
  },
  2: {
    clave: "preparando",
    label: "En preparación",
    corto: "Preparando",
    tone: "info",
    icon: "box",
    accion: { a: 3, label: "Despachar", tone: "primary" },
    espera: "Aceptado. Falta asignarle un domiciliario",
  },
  3: {
    clave: "en_camino",
    label: "En camino",
    corto: "En camino",
    tone: "info",
    icon: "bike",
    accion: null,
    espera: "Lo lleva el domiciliario. La entrega la declara él",
  },
  4: {
    clave: "entregado",
    label: "Entregado",
    corto: "Entregados",
    tone: "ok",
    icon: "check",
    accion: null,
    espera: null,
  },
  5: {
    clave: "cancelado",
    label: "Cancelado",
    corto: "Cancelados",
    tone: "bad",
    icon: "close",
    accion: null,
    espera: null,
  },
};

/** El orden en que se recorren las columnas del tablero. Sin el 5: un pedido
    cancelado no es una etapa del trabajo, es algo que dejó de existir, y darle
    una columna fija lo pondría a competir por la atención con los que sí hay
    que atender. Se consultan aparte. */
export const FLUJO_DEL_PEDIDO = [1, 2, 3, 4];

/** Cuántas filas por página ofrece la tabla. Lo usa `DataTable`. */
export const PAGE_SIZES = [10, 25, 50, 100];
